import { Injectable } from "@angular/core";
import { FinalRatingsData, OBCommentsReq } from "src/app/models/models";
import { DraftService } from "src/app/shared/services/draft.service";

export interface IObservationRating {
	observationCode: string;
	maneuverCode: string;
	totalAttempt: number;
	activeAttempt: number;
	isRemoved: boolean | null;
}

@Injectable({
	providedIn: "root",
})
export class OfflineRatingService {
	constructor(private draftService: DraftService) {}

	calculate(candidateId: string): FinalRatingsData {
		// gets the selected draft
		const draft = this.draftService.draft;
		// init default return value
		let result: FinalRatingsData = {
			ratingA: 1,
			ratingB: 1,
			ratingC: 1,
			finalRating: 1,
		};

		if (draft) {
			const candidateRating = draft?.candidateRating;

			if (candidateRating) {
				const temRate = candidateRating?.temRate ?? {};
				const temScores = [];

				for (const key in temRate) {
					const cId = key.split(".")[0];
					const data = temRate[key];

					if (cId === candidateId) {
						temScores.push({
							...data,
							candidateId: cId,
						});
					}
				}

				// exclude maneuver rated 0 and -1
				const { zeroRated: excludeManeuverIds, nonZeroRated } = temScores.reduce<{
					zeroRated: string[];
					nonZeroRated: number[];
				}>(
					(acc, prevValue) => {
						if (prevValue.rate === 0 || prevValue.rate === -1) {
							acc.zeroRated.push(prevValue.assessmentId);
						} else {
							acc.nonZeroRated.push(prevValue.rate);
						}
						return acc;
					},
					{ zeroRated: [], nonZeroRated: [] },
				);

				// obs history
				const observationHistoryUnfiltered = (candidateRating?.addRemoveOb ?? []).filter((o) => {
					const isRepeat = o.isRepeat ?? false;

					return o.candidateId === candidateId && !isRepeat;
				});

				// to make sure only one category, and override older values
				const observationHistoryMap: { [id: string]: OBCommentsReq } = {};
				for (const o of observationHistoryUnfiltered) {
					observationHistoryMap[`${o.maneuverCode}.${o.observationCode}`] = o;
				}

				const observationHistory = Object.values(observationHistoryMap);

				const form = draft?.formDetails;
				const assessment = form?.assessmentConfig ?? [];

				// get all predefined OBs except from the 0-rated maneuver
				const meneuverCodeToExclude: string[] = [];
				const [, predefinedObservations] = assessment.reduce<
					[
						string[],
						{
							id: string;
							categoryId: string;
							totalAttempt: number;
							activeAttempt: number;
							observationCode: string;
							maneuverCode: string;
						}[],
					]
				>(
					(acc, { maneuverType, observations, assessmentId }) => {
						if (excludeManeuverIds.includes(assessmentId)) {
							meneuverCodeToExclude.push(maneuverType.code);
						}

						if (!excludeManeuverIds.includes(assessmentId)) {
							observations.map((observation) => {
								acc[0].push(observation.code);
								acc[1].push({
									id: observation.code,
									categoryId: `${maneuverType.code}.${observation.category.code}`,
									totalAttempt: observation.attempt ?? 1,
									activeAttempt: observation.attempt ?? 1,
									observationCode: observation.code,
									maneuverCode: maneuverType.code,
								});
							});
						}
						return acc;
					},
					[[], []],
				);

				// get the total of additional obs added
				const observationHistoryAltered = observationHistory.map((observation) => {
					return {
						id: observation.observationCode,
						maneuver: observation.maneuverCode,
						totalAttempt: observation.totalAttempt ?? 1,
						activeAttempt: observation.activeAttempt ?? 1,
						observationCode: observation.observationCode,
						maneuverCode: observation.maneuverCode,
						isRemoved: observation.isRemoved,
					};
				});

				const PredefinedObs = predefinedObservations?.map((v) => {
					return {
						observationCode: v.observationCode,
						maneuverCode: v.maneuverCode,
						totalAttempt: v.totalAttempt,
						activeAttempt: v.activeAttempt,
						isRemoved: false,
					};
				});

				const NewObs = observationHistoryAltered?.map((v) => {
					return {
						observationCode: v.observationCode,
						maneuverCode: v.maneuverCode,
						totalAttempt: v.totalAttempt,
						activeAttempt: v.activeAttempt,
						isRemoved: v.isRemoved,
					};
				});

				// rating A
				const ratingA = this.calculateRatingA({
					maneuverCodeToExclude: [...new Set(meneuverCodeToExclude)],
					predefinedObs: [...PredefinedObs],
					newObs: [...NewObs],
				});

				// new rating B
				const ratingB = this.calculateRatingB({
					predefinedObs: [...PredefinedObs],
					newObs: [...NewObs],
					maneuverCodeToExclude: [...new Set(meneuverCodeToExclude)],
				});

				const ratingC = this.calculateRatingC(nonZeroRated);

				const finalRating = Math.min(ratingA, ratingB, ratingC);

				result = { ratingA, ratingB, ratingC, finalRating };
			} else {
				console.error(`No rating to be calculated for this draft.`);
			}
		} else {
			console.error(`No selected draft or draft does not exists`);
		}

		return result;
	}

	private ratingScale = (score: number) => {
		// See shared rating-scales.ts: clamp so How-Often completion >100% maps to 5.
		score = Math.min(score, 100);
		if (score <= 60) return 1;
		if (score <= 70) return 2;
		if (score <= 80) return 3;
		if (score <= 90) return 4;
		if (score <= 100) return 5;
		return 1;
	};

	calculateRatingA = ({
		newObs = [],
		predefinedObs = [],
		maneuverCodeToExclude = [],
	}: {
		predefinedObs: IObservationRating[];
		newObs: IObservationRating[];
		maneuverCodeToExclude?: string[];
	}) => {
		//to exclude maneuver
		const filteredPredefinedObs = predefinedObs?.filter(({ maneuverCode }) => {
			return !maneuverCodeToExclude?.includes(maneuverCode);
		});
		const filteredNewObs = newObs?.filter(({ maneuverCode }) => {
			return !maneuverCodeToExclude?.includes(maneuverCode);
		});

		// only get unique values; remove duplicate obs from different assessments
		const predefinedObservationCodes = filteredPredefinedObs?.map((v) => v.observationCode);
		const uniquePredefinedObservationCodes = [...new Set(predefinedObservationCodes)];

		// get the total of additional obs added
		const _additionalObs =
			filteredNewObs
				.filter(({ observationCode }) => !uniquePredefinedObservationCodes.includes(observationCode))
				?.filter(Boolean)?.length ?? 0;

		const _predefinedObservationCount = predefinedObservationCodes?.length ?? 0;

		const _removedObsCount = filteredNewObs.filter(({ isRemoved }) => isRemoved)?.length ?? 0;

		const totalObs = _additionalObs + _predefinedObservationCount;
		const _ratingA = Math.round(((totalObs - _removedObsCount) / totalObs) * 100);
		return this.ratingScale(_ratingA);
	};

	calculateRatingB = ({
		newObs = [],
		predefinedObs = [],
		maneuverCodeToExclude = [],
	}: {
		predefinedObs: IObservationRating[];
		newObs: IObservationRating[];
		maneuverCodeToExclude?: string[];
	}) => {
		console.log("Maneuver to exclude from calculation : ", maneuverCodeToExclude?.join(","));

		//to exclude maneuver
		const filteredPredefinedObs = predefinedObs?.filter(({ maneuverCode }) => {
			return !maneuverCodeToExclude?.includes(maneuverCode);
		});
		const filteredNewObs = newObs?.filter(({ maneuverCode }) => {
			return !maneuverCodeToExclude?.includes(maneuverCode);
		});

		// Add predefined observations grouped by derived category
		const categoryMap: Record<string, IObservationRating[]> = {};
		filteredPredefinedObs?.forEach((obs) => {
			const category = obs?.observationCode?.split("_")[0];
			if (!categoryMap[category]) {
				categoryMap[category] = [];
			}
			categoryMap[category].push(obs);
		});

		// Overwrite or add new observations in the categoryMap
		filteredNewObs?.forEach((obs) => {
			const category = obs?.observationCode?.split("_")[0];
			const observations = categoryMap[category] || [];

			const uniqueObservations = observations?.filter(
				(existing) => existing.observationCode !== obs.observationCode || existing.maneuverCode !== obs.maneuverCode,
			);

			uniqueObservations.push(obs);
			categoryMap[category] = uniqueObservations;
		});

		// Consolidate observations by category, using newObs to overwrite predefinedObs when needed
		const _ratingB: { obCategoryCode: string; rating: number }[] = [];

		// Calculate the average rating per category
		for (const [category, observations] of Object.entries(categoryMap)) {
			const sumActiveOb = observations?.reduce((total, curr) => total + curr.activeAttempt, 0);

			const totalAttemptOb = observations?.reduce((total, curr) => total + curr.totalAttempt, 0);

			const categoryRating = sumActiveOb / totalAttemptOb;
			const rating = this.ratingScale(categoryRating * 100);
			observations?.forEach((obs) =>
				console.log(
					`Observation Code: ${obs.observationCode}, Maneuver Code: ${obs.maneuverCode}, Active Attempts: ${obs.activeAttempt}, Total Attempts: ${obs.totalAttempt}`,
				),
			);
			console.log(
				`Category: ${category}, sumActiveOb: ${sumActiveOb}, totalAttemptOb: ${totalAttemptOb},percentage : ${
					categoryRating * 100
				}, rating: ${rating}`,
			);
			_ratingB.push({ obCategoryCode: category, rating });
		}
		const ratingB = Math.round(_ratingB?.reduce((acc, { rating }) => acc + rating, 0) / _ratingB?.length);
		// Calculate the overall average rating
		console.log("Rating B : ", ratingB);
		return Number.isNaN(ratingB) ? 1 : ratingB;
	};

	calculateRatingC = (data: number[]) => {
		if (data?.length === 0) return 1;

		if (data?.includes(1)) return 1;

		const sum = data?.reduce((acc, prevValue) => acc + prevValue, 0) / data?.length;

		const decimalPart = +(sum % 1).toFixed(4);
		return decimalPart > 0.6 ? Math.ceil(sum) : Math.floor(sum);
	};
}
